import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { ClienteNuevo, ModuloDisponible, Plan } from "src/app/shared/model/security/plan.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { PlataformaService } from "../../plataforma.service";

/**
 * Alta de un cliente completo: corporativo, primera empresa, primer
 * administrador y los módulos con los que arranca.
 *
 * ES UNA SOLA OPERACIÓN A PROPÓSITO
 * Un corporativo sin empresa, o una empresa sin administrador, es un cliente
 * que existe en la base y en el que nadie puede entrar. El servidor lo hace
 * todo o nada; aquí se pide todo junto para que no haya un «después».
 *
 * El administrador recibe todos los perfiles de cada módulo contratado. Con
 * ellos entra, ve el módulo entero y da de alta a su gente desde Personal.
 */
@Component({
  selector: "app-modal-cliente",
  standalone: true,
  templateUrl: "./modal-cliente.component.html",
  styleUrls: ["./modal-cliente.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class ModalClienteComponent implements OnInit {
  alta = new ClienteNuevo();
  /** Catálogo entero; el cliente es nuevo, así que no tiene nada vigente. */
  modulos: ModuloDisponible[] = [];
  /** Nombres de los módulos elegidos; el cupo y la vigencia van por renglón. */
  elegidos: string[] = [];
  cargandoModulos = false;
  guardando = false;

  constructor(
    private dialogRef: MatDialogRef<ModalClienteComponent>,
    private plataformaService: PlataformaService,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarModulos();
  }

  /** Al (des)marcar módulos, la lista de planes sigue a la selección sin perder lo capturado. */
  alCambiarModulos(): void {
    const previos = new Map(this.alta.planes.map((plan) => [plan.sitio, plan]));
    this.alta.planes = this.elegidos.map((sitio) => {
      const previo = previos.get(sitio);
      if (previo) {
        return previo;
      }
      const plan = new Plan();
      plan.sitio = sitio;
      plan.cantidadUsuarios = 50;
      plan.vigencia = this.dentroDeUnAnio();
      return plan;
    });
  }

  get planesValidos(): boolean {
    return (
      this.alta.planes.length > 0 &&
      this.alta.planes.every((plan) => (plan.cantidadUsuarios ?? 0) >= 1 && !!plan.vigencia)
    );
  }

  get passwordCorta(): boolean {
    return this.alta.password.length > 0 && this.alta.password.length < 8;
  }

  get puedeGuardar(): boolean {
    const a = this.alta;
    return (
      !this.guardando &&
      !!a.nombreCorporativo.trim() &&
      !!a.razonSocial.trim() &&
      !!a.usuario.trim() &&
      a.password.length >= 8 &&
      !!a.nombre.trim() &&
      !!a.apellidoPaterno.trim() &&
      !!a.correoAdministrador.trim() &&
      this.planesValidos
    );
  }

  cancelar(): void {
    this.dialogRef.close(false);
  }

  guardar(): void {
    if (!this.puedeGuardar) {
      return;
    }
    this.guardando = true;
    this.cdr.detectChanges();

    this.plataformaService.crearCliente(this.alta).subscribe({
      next: (respuesta) => {
        this.guardando = false;
        if (respuesta?.pCodRetorno && respuesta.pCodRetorno !== "00000") {
          this.msg.warningCall(respuesta.pMensaje, null, null);
          this.cdr.detectChanges();
          return;
        }
        this.msg.successCall(
          `${this.alta.nombreCorporativo} quedó dado de alta. Su administrador ya puede entrar con el usuario ${this.alta.usuario}.`,
          null,
          null
        );
        this.dialogRef.close(true);
      },
      error: () => {
        this.guardando = false;
        this.msg.errorCall("No se pudo dar de alta el cliente.", null, null);
        this.cdr.detectChanges();
      },
    });
  }

  // ------------------------------------------------------------------

  /**
   * El catálogo se pide con un corporativo que no existe (0): para un
   * cliente nuevo «disponible» es todo el catálogo, y el backend ya excluye
   * PLATAFORMA.
   */
  private cargarModulos(): void {
    this.cargandoModulos = true;
    this.plataformaService.consultarModulosDisponibles(0).subscribe({
      next: (respuesta) => {
        this.modulos = respuesta?.list ?? [];
        this.cargandoModulos = false;
        if (respuesta?.pMensaje && respuesta.pCodRetorno !== "00000") {
          this.msg.warningCall(respuesta.pMensaje, null, null);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.modulos = [];
        this.cargandoModulos = false;
        this.msg.errorCall("No se pudo consultar el catálogo de módulos.", null, null);
        this.cdr.detectChanges();
      },
    });
  }

  private dentroDeUnAnio(): string {
    const fecha = new Date();
    fecha.setFullYear(fecha.getFullYear() + 1);
    const mes = `${fecha.getMonth() + 1}`.padStart(2, "0");
    const dia = `${fecha.getDate()}`.padStart(2, "0");
    return `${fecha.getFullYear()}-${mes}-${dia}`;
  }
}
